/**
 * Placement Cascade tab component with cascade visualization.
 */

import { useState } from 'react';
import { useCascade } from '@/hooks/useAnalytics';
import { LoadingSpinner } from '@/components/ui/loading';
import { Card } from '@/components/ui/card';
import './cascade.scss';

interface PlacementCascadeProps {
  placementId: string;
}

export function PlacementCascade({ placementId }: PlacementCascadeProps) {
  const [window, setWindow] = useState<'24h' | '7d' | '30d'>('24h');
  const { data, isLoading, error } = useCascade(placementId, window);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="of-text-muted">Error loading cascade data</p>
      </Card>
    );
  }

  if (!data || data.nodes.length === 0) {
    return (
      <div className="placement-cascade">
        <div className="placement-cascade__controls">
          <label htmlFor="window-select" className="label">
            Time Window
          </label>
          <select
            id="window-select"
            value={window}
            onChange={(e) => setWindow(e.target.value as '24h' | '7d' | '30d')}
            className="select"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
        <Card>
          <p className="of-text-muted">No cascade data available</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="placement-cascade">
      <div className="placement-cascade__controls">
        <label htmlFor="window-select" className="label">
          Time Window
        </label>
        <select
          id="window-select"
          value={window}
          onChange={(e) => setWindow(e.target.value as '24h' | '7d' | '30d')}
          className="select"
        >
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>
      <Card>
        <h3 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>
          Cascade Graph
        </h3>
        <div className="placement-cascade__info">
          <p>Cascade visualization will be implemented with D3.js</p>
          <div className="placement-cascade__stats">
            <p>Nodes: {data.nodes.length}</p>
            <p>Edges: {data.edges.length}</p>
          </div>
          {data.nodes.length > 0 && (
            <div className="placement-cascade__nodes-preview">
              <h4>Top Nodes:</h4>
              <ul>
                {data.nodes.slice(0, 5).map((node) => (
                  <li key={node.node_id}>
                    {node.ref_host} - Spark: {node.spark_score?.toFixed(2) || 'N/A'} - Descendants: {node.descendants}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

